import React, { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Palmtree, CalendarOff, Hourglass, Check, X, Users, AlertTriangle, Plus,
         ChevronLeft, ChevronRight, Clock3, Home, FileUp, FileText, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "@/components/ui/dropdown-menu";
import { api, apiErrorMessage } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";

const KIND_LABEL = { vacaciones: "Vacaciones", ausencia: "Ausencia" };
function fmtEs(iso) { if (!iso) return ""; const [y, m, d] = iso.split("-"); return `${d}/${m}/${y}`; }
function rangeEs(a, b) { return a === b ? fmtEs(a) : `${fmtEs(a)} → ${fmtEs(b)}`; }

export default function VacacionesTab() {
  const qc = useQueryClient();
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [openKind, setOpenKind] = useState(null); // "vacaciones" | "ausencia" | "teletrabajo_excepcional"

  const meQ = useQuery({ queryKey: ["rrhh-absence", year], queryFn: async () => (await api.get("/rrhh/absence", { params: { year } })).data });
  const data = meQ.data;
  const canApprove = !!data?.can_approve;
  const rows = useMemo(() => data?.rows || [], [data]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["rrhh-absence"] });
    qc.invalidateQueries({ queryKey: ["rrhh-absence-requests"] });
    qc.invalidateQueries({ queryKey: ["rrhh-absence-team"] });
    qc.invalidateQueries({ queryKey: ["rrhh-hybrid"] });
    qc.invalidateQueries({ queryKey: ["rrhh-cal-month"] });
    qc.invalidateQueries({ queryKey: ["rrhh-justif-pending"] });
    qc.invalidateQueries({ queryKey: ["rrhh-remote"] });
  };

  const reqAbsence = useMutation({
    mutationFn: async (p) => (await api.post("/rrhh/absence", p)).data,
    onSuccess: (r) => {
      invalidate(); setOpenKind(null);
      toast.success(r.kind === "vacaciones" ? "Vacaciones solicitadas" : (r.is_partial ? "Ausencia por horas solicitada" : "Ausencia solicitada"));
      (r.warnings || []).forEach((w) => toast.warning(w));
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const reqRemote = useMutation({
    mutationFn: async (p) => (await api.post("/rrhh/remote", { ...p, exceptional: true })).data,
    onSuccess: (r) => {
      invalidate(); setOpenKind(null);
      toast.success("Teletrabajo excepcional solicitado (no descuenta del cupo)");
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const cancelMut = useMutation({
    mutationFn: async (aid) => (await api.delete(`/rrhh/absence/${aid}`)).data,
    onSuccess: () => { invalidate(); toast.success("Solicitud cancelada"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-6" data-testid="rrhh-vacaciones">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-bold tracking-tight"><Palmtree className="h-6 w-6 text-sky-600" />Vacaciones y ausencias</h1>
          <p className="text-sm text-muted-foreground">22 días laborables/año. Ausencias por horas (médico, gestión…) o día completo. Teletrabajo excepcional no descuenta cupo. Aprueban Dirección y Administración.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y - 1)} data-testid="vac-year-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="w-12 text-center text-sm font-semibold" data-testid="vac-year">{year}</span>
            <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => setYear((y) => y + 1)} data-testid="vac-year-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="bg-sky-600 hover:bg-sky-700" data-testid="vac-new-btn"><Plus className="mr-1.5 h-4 w-4" />Nueva solicitud</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <DropdownMenuItem onClick={() => setOpenKind("vacaciones")} data-testid="menu-vacaciones">
                <Palmtree className="mr-2 h-4 w-4 text-sky-600" /><div className="flex-1"><p className="text-sm font-medium">Vacaciones</p><p className="text-[10px] text-muted-foreground">Descuenta del cupo anual</p></div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenKind("ausencia")} data-testid="menu-ausencia">
                <CalendarOff className="mr-2 h-4 w-4 text-orange-600" /><div className="flex-1"><p className="text-sm font-medium">Ausencia</p><p className="text-[10px] text-muted-foreground">Día completo o por horas</p></div>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setOpenKind("teletrabajo_excepcional")} data-testid="menu-teletrab-exc">
                <Sparkles className="mr-2 h-4 w-4 text-purple-600" /><div className="flex-1"><p className="text-sm font-medium">Teletrabajo excepcional</p><p className="text-[10px] text-muted-foreground">NO descuenta del cupo de 10 días</p></div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Cupo anual" value={data?.vacation_allowance ?? 22} testid="vac-stat-allowance" />
        <StatCard label="Disfrutadas" value={data?.vacaciones_used ?? 0} tone="emerald" testid="vac-stat-used" />
        <StatCard label="Pendientes" value={data?.vacaciones_pending ?? 0} tone="amber" testid="vac-stat-pending" />
        <StatCard label="Restantes" value={data?.vacaciones_remaining ?? 22} tone="sky" testid="vac-stat-remaining" />
      </div>

      <section>
        <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mis vacaciones y ausencias {year}</h2>
        {meQ.isLoading ? <Skeleton className="h-24 w-full" /> : rows.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="vac-empty">No tienes vacaciones ni ausencias este año.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="vac-my-list">
            {rows.map((r) => <MyAbsenceRow key={r.id} r={r} onCancel={() => cancelMut.mutate(r.id)} onChanged={invalidate} />)}
          </div>
        )}
      </section>

      {canApprove && <VacApproverPanel year={year} onChange={invalidate} />}

      {openKind && (
        <SolicitudDialog
          initialKind={openKind}
          today={data?.today}
          saving={reqAbsence.isPending || reqRemote.isPending}
          onClose={() => setOpenKind(null)}
          onSave={(payload) => {
            if (payload._route === "remote") {
              const { _route, ...rest } = payload; reqRemote.mutate(rest);
            } else {
              const { _route, ...rest } = payload; reqAbsence.mutate(rest);
            }
          }}
        />
      )}
    </div>
  );
}

// -------------------------------------------------- fila con acciones justificante
function MyAbsenceRow({ r, onCancel, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const uploadJustif = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/rrhh/absence/${r.id}/justificante`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Justificante subido correctamente");
      onChanged?.();
    } catch (e) { toast.error(apiErrorMessage(e)); }
    finally { setUploading(false); }
  };
  const kindLabel = KIND_LABEL[r.kind] || r.kind;
  const justUrl = `${process.env.REACT_APP_BACKEND_URL}/api/rrhh/absence/${r.id}/justificante`;
  return (
    <div className={`space-y-1 rounded-lg border bg-card p-3 ${r.justificante_required && !r.justificante_file_id ? "border-amber-300 bg-amber-50/40" : ""}`}
         data-testid={`vac-item-${r.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <span className={`flex h-9 w-9 items-center justify-center rounded-lg ${r.kind === "vacaciones" ? "bg-sky-100 text-sky-600" : "bg-orange-100 text-orange-600"}`}>
            {r.kind === "vacaciones" ? <Palmtree className="h-4 w-4" /> : <CalendarOff className="h-4 w-4" />}
          </span>
          <div>
            <p className="text-sm font-medium">
              {kindLabel} · {rangeEs(r.start, r.end)}
              {r.is_partial && r.time_start && r.time_end && (
                <span className="ml-2 inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700"><Clock3 className="h-3 w-3" />{r.time_start}–{r.time_end}</span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {r.is_partial ? "Ausencia por horas · no descuenta vacaciones" : `${r.labor_days} día${r.labor_days === 1 ? "" : "s"} laborable${r.labor_days === 1 ? "" : "s"}`}
              {r.reason ? ` · ${r.reason}` : ""}
              {r.status === "aprobado" && r.approver_message ? ` · 💬 ${r.approver_message}` : ""}
              {r.status === "rechazado" && r.reject_reason ? ` · ${r.reject_reason}` : ""}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={r.status} />
          {r.status !== "rechazado" && (
            <Button variant="ghost" size="sm" className="h-8 text-red-600 hover:text-red-700" onClick={onCancel} data-testid={`vac-cancel-${r.id}`}><X className="h-4 w-4" /></Button>
          )}
        </div>
      </div>
      {r.justificante_required && (
        <div className={`flex items-center justify-between rounded-md border px-2 py-1.5 text-xs ${r.justificante_file_id ? "border-emerald-300 bg-emerald-50 text-emerald-800" : "border-amber-300 bg-amber-50 text-amber-900"}`}
             data-testid={`vac-justif-${r.id}`}>
          <span className="flex items-center gap-1.5">
            {r.justificante_file_id
              ? <><Check className="h-3.5 w-3.5" /><strong>Justificante subido</strong> · {r.justificante_file_name || "archivo"}</>
              : <><AlertTriangle className="h-3.5 w-3.5" /><strong>Justificante pendiente</strong> · sube PDF, imagen o Word (máx 10 MB)</>}
          </span>
          <div className="flex items-center gap-2">
            {r.justificante_file_id && <a href={justUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-medium hover:underline" data-testid={`vac-justif-view-${r.id}`}><FileText className="h-3.5 w-3.5" />Ver</a>}
            <label className="inline-flex cursor-pointer items-center gap-1 rounded-md border border-current px-2 py-0.5 hover:bg-white/70" data-testid={`vac-justif-upload-${r.id}`}>
              <FileUp className="h-3 w-3" />{r.justificante_file_id ? "Reemplazar" : "Subir"}
              <input type="file" accept="application/pdf,image/*,.doc,.docx" hidden onChange={(e) => uploadJustif(e.target.files?.[0])} disabled={uploading} />
            </label>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, tone = "slate", testid }) {
  const tones = { slate: "text-foreground", emerald: "text-emerald-600", amber: "text-amber-600", sky: "text-sky-600" };
  return (
    <Card><CardContent className="p-4">
      <p className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`mt-1 text-2xl font-bold ${tones[tone]}`} data-testid={testid}>{value}</p>
    </CardContent></Card>
  );
}

function StatusBadge({ status }) {
  if (status === "aprobado") return <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Aprobado</Badge>;
  if (status === "rechazado") return <Badge variant="outline" className="border-red-200 text-red-600">Rechazado</Badge>;
  return <Badge variant="outline" className="border-amber-300 text-amber-700">Pendiente</Badge>;
}

// -------------------------------------------------- Dialog de solicitud (3-en-1)
function SolicitudDialog({ initialKind, today, onClose, onSave, saving }) {
  const [start, setStart] = useState(today || "");
  const [end, setEnd] = useState(today || "");
  const [reason, setReason] = useState("");
  const [mode, setMode] = useState("full"); // "full" | "partial"  (solo para "ausencia")
  const [timeStart, setTimeStart] = useState("09:00");
  const [timeEnd, setTimeEnd] = useState("11:00");

  const isVac = initialKind === "vacaciones";
  const isAus = initialKind === "ausencia";
  const isRemote = initialKind === "teletrabajo_excepcional";

  const validPartial = mode === "partial" && start && start === end && timeStart && timeEnd && timeEnd > timeStart;
  const validFull = mode === "full" && start && end && end >= start;
  const validRemote = isRemote && start;
  const valid = isRemote ? validRemote : (isAus ? (mode === "partial" ? validPartial : validFull) : validFull);
  const reasonRequired = isAus && !reason;
  const canSubmit = valid && !saving && !reasonRequired;

  const submit = () => {
    if (isRemote) {
      onSave({ _route: "remote", date: start, note: reason });
      return;
    }
    const base = { _route: "absence", kind: initialKind, start, end, reason };
    if (isAus && mode === "partial") { base.end = start; base.time_start = timeStart; base.time_end = timeEnd; }
    onSave(base);
  };

  const title = isVac ? "Solicitar vacaciones"
    : isAus ? "Solicitar ausencia"
    : "Teletrabajo excepcional";
  const icon = isVac ? <Palmtree className="h-5 w-5 text-sky-600" />
    : isAus ? <CalendarOff className="h-5 w-5 text-orange-600" />
    : <Sparkles className="h-5 w-5 text-purple-600" />;
  const desc = isVac ? "Elige el rango. Se avisará si hay solapes con tu equipo, pero deciden Dirección y Administración."
    : isAus ? "Puedes indicar día completo o una franja horaria (ej. cita médica). Las ausencias por horas NO descuentan de tu cupo de vacaciones."
    : "Trabajo desde casa por un motivo puntual (técnico, cita médica, gestión). NO se descuenta de tus 10 días de teletrabajo anuales.";

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="vac-request-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">{icon}{title}</DialogTitle>
          <DialogDescription>{desc}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {isAus && (
            <div className="flex overflow-hidden rounded-lg border" data-testid="vac-mode-toggle">
              <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${mode === "full" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                onClick={() => setMode("full")} data-testid="mode-full">Día completo</button>
              <button className={`flex-1 px-3 py-1.5 text-xs font-medium ${mode === "partial" ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                onClick={() => { setMode("partial"); setEnd(start); }} data-testid="mode-partial">Franja horaria</button>
            </div>
          )}

          {isRemote ? (
            <div className="space-y-1.5">
              <Label>Día</Label>
              <Input type="date" value={start} min={today} onChange={(e) => setStart(e.target.value)} data-testid="vac-start" />
            </div>
          ) : mode === "partial" ? (
            <>
              <div className="space-y-1.5">
                <Label>Día</Label>
                <Input type="date" value={start} min={today} onChange={(e) => { setStart(e.target.value); setEnd(e.target.value); }} data-testid="vac-start" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Hora inicio</Label><Input type="time" value={timeStart} onChange={(e) => setTimeStart(e.target.value)} data-testid="vac-time-start" /></div>
                <div className="space-y-1.5"><Label>Hora fin</Label><Input type="time" value={timeEnd} onChange={(e) => setTimeEnd(e.target.value)} data-testid="vac-time-end" /></div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Desde</Label><Input type="date" value={start} min={today} onChange={(e) => { setStart(e.target.value); if (e.target.value > end) setEnd(e.target.value); }} data-testid="vac-start" /></div>
              <div className="space-y-1.5"><Label>Hasta</Label><Input type="date" value={end} min={start} onChange={(e) => setEnd(e.target.value)} data-testid="vac-end" /></div>
            </div>
          )}

          <div className="space-y-1.5">
            <Label>{isAus ? "Motivo *" : isRemote ? "Motivo (para Administración)" : "Nota (opcional)"}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
              placeholder={isAus ? "Ej.: cita médica, gestión personal, familiar…" : isRemote ? "Ej.: viene el técnico a casa" : "Comentario para Administración"}
              data-testid="vac-note" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className={isRemote ? "bg-purple-600 hover:bg-purple-700" : (isAus ? "bg-orange-600 hover:bg-orange-700" : "bg-sky-600 hover:bg-sky-700")}
            onClick={submit} disabled={!canSubmit} data-testid="vac-submit">{saving ? "Enviando…" : "Enviar solicitud"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// -------------------------------------------------- Panel del aprobador
function VacApproverPanel({ year, onChange }) {
  const qc = useQueryClient();
  const [decision, setDecision] = useState(null);
  const reqsQ = useQuery({ queryKey: ["rrhh-absence-requests"], queryFn: async () => (await api.get("/rrhh/absence/requests", { params: { status: "pendiente" } })).data });
  const teamQ = useQuery({ queryKey: ["rrhh-absence-team", year], queryFn: async () => (await api.get("/rrhh/absence/team", { params: { year } })).data });
  const justifQ = useQuery({ queryKey: ["rrhh-justif-pending"], queryFn: async () => (await api.get("/rrhh/absence/justif-pending")).data });

  const after = () => {
    qc.invalidateQueries({ queryKey: ["rrhh-absence-requests"] });
    qc.invalidateQueries({ queryKey: ["rrhh-absence-team"] });
    qc.invalidateQueries({ queryKey: ["rrhh-absence"] });
    qc.invalidateQueries({ queryKey: ["rrhh-hybrid"] });
    qc.invalidateQueries({ queryKey: ["rrhh-cal-month"] });
    qc.invalidateQueries({ queryKey: ["rrhh-justif-pending"] });
    onChange?.(); setDecision(null);
  };
  const approveMut = useMutation({
    mutationFn: async ({ aid, message, justificante_required }) => (await api.post(`/rrhh/absence/${aid}/approve`, { message, justificante_required })).data,
    onSuccess: () => { after(); toast.success("Aprobada · aviso enviado a la persona"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const rejectMut = useMutation({
    mutationFn: async ({ aid, reason }) => (await api.post(`/rrhh/absence/${aid}/reject`, { reason })).data,
    onSuccess: () => { after(); toast.success("Rechazada · aviso enviado a la persona"); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const saving = approveMut.isPending || rejectMut.isPending;
  const confirmDecision = (message, justificante_required) => {
    if (!decision) return;
    if (decision.mode === "approve") approveMut.mutate({ aid: decision.item.id, message, justificante_required });
    else rejectMut.mutate({ aid: decision.item.id, reason: message });
  };

  const pending = reqsQ.data?.rows || [];
  const justifRows = justifQ.data?.rows || [];

  return (
    <>
      <section className="border-t pt-5" data-testid="vac-approver">
        <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Hourglass className="h-3.5 w-3.5" />Solicitudes pendientes ({pending.length})</h2>
        {reqsQ.isLoading ? <Skeleton className="h-20 w-full" /> : pending.length === 0 ? (
          <Card><CardContent className="p-6 text-center text-sm text-muted-foreground" data-testid="vac-no-pending">No hay solicitudes pendientes.</CardContent></Card>
        ) : (
          <div className="space-y-2" data-testid="vac-pending-list">
            {pending.map((r) => (
              <div key={r.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card p-3" data-testid={`vac-pending-${r.id}`}>
                <div className="flex items-center gap-2.5">
                  <UserAvatar name={r.user?.name} color={r.user?.color} size="sm" />
                  <div>
                    <p className="text-sm font-medium">{r.user?.name} · {KIND_LABEL[r.kind]}{r.is_partial ? " (por horas)" : ""}</p>
                    <p className="text-xs text-muted-foreground">
                      {rangeEs(r.start, r.end)}
                      {r.is_partial && r.time_start && r.time_end ? ` · ${r.time_start}–${r.time_end}` : ` · ${r.labor_days} día${r.labor_days === 1 ? "" : "s"}`}
                      {r.reason ? ` · ${r.reason}` : ""}
                    </p>
                    {(r.warnings || []).length > 0 && (
                      <ul className="mt-1 space-y-0.5" data-testid={`vac-warn-${r.id}`}>
                        {r.warnings.map((w, i) => (
                          <li key={i} className="flex items-start gap-1 text-[11px] font-medium text-amber-600"><AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />{w}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button size="sm" variant="outline" className="h-8 border-red-200 text-red-600 hover:bg-red-50" onClick={() => setDecision({ item: r, mode: "reject" })} data-testid={`vac-reject-${r.id}`}><X className="mr-1 h-3.5 w-3.5" />Rechazar</Button>
                  <Button size="sm" className="h-8 bg-emerald-600 hover:bg-emerald-700" onClick={() => setDecision({ item: r, mode: "approve" })} data-testid={`vac-approve-${r.id}`}><Check className="mr-1 h-3.5 w-3.5" />Aprobar</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {justifRows.length > 0 && (
        <section data-testid="justif-panel">
          <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><FileText className="h-3.5 w-3.5" />Justificantes ({justifRows.length})</h2>
          <div className="space-y-2">
            {justifRows.map((r) => (
              <div key={r.id} className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-3 ${r.justificante_file_id ? "border-emerald-200 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`} data-testid={`justif-row-${r.id}`}>
                <div className="flex items-center gap-2.5">
                  <UserAvatar name={r.user?.name} color={r.user?.color} size="sm" />
                  <div>
                    <p className="text-sm font-medium">{r.user?.name} · {KIND_LABEL[r.kind]}</p>
                    <p className="text-xs text-muted-foreground">{rangeEs(r.start, r.end)}{r.is_partial && r.time_start ? ` · ${r.time_start}–${r.time_end}` : ""}{r.reason ? ` · ${r.reason}` : ""}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs">
                  {r.justificante_file_id ? (
                    <a href={`${process.env.REACT_APP_BACKEND_URL}/api/rrhh/absence/${r.id}/justificante`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 rounded-md border border-emerald-300 bg-white px-2 py-1 font-medium text-emerald-700 hover:bg-emerald-50" data-testid={`justif-view-${r.id}`}>
                      <FileText className="h-3.5 w-3.5" />Ver justificante ({r.justificante_file_name || "archivo"})
                    </a>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 font-medium text-amber-800">
                      <Hourglass className="h-3.5 w-3.5" />Pendiente de subida
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      <section data-testid="vac-team-table">
        <h2 className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"><Users className="h-3.5 w-3.5" />Vacaciones del equipo {year}</h2>
        {teamQ.isLoading ? <Skeleton className="h-40 w-full" /> : (
          <Card><CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
                <tr><th className="p-3">Persona</th><th className="p-3">Equipo</th><th className="p-3 text-center">Disfrutadas</th><th className="p-3 text-center">Pendientes</th><th className="p-3 text-center">Restantes</th><th className="p-3">Próximas</th></tr>
              </thead>
              <tbody className="divide-y">
                {(teamQ.data?.rows || []).map((u) => (
                  <tr key={u.id} data-testid={`vac-team-${u.id}`}>
                    <td className="p-3"><span className="flex items-center gap-2"><UserAvatar name={u.name} color={u.color} size="xs" />{u.name}</span></td>
                    <td className="p-3 text-muted-foreground">{u.equipo || "—"}</td>
                    <td className="p-3 text-center font-semibold text-emerald-600">{u.vac_used}</td>
                    <td className="p-3 text-center text-amber-600">{u.vac_pending}</td>
                    <td className="p-3 text-center text-muted-foreground">{u.remaining}/{u.allowance}</td>
                    <td className="p-3 text-xs text-muted-foreground">
                      {(u.upcoming || []).length === 0 ? "—" : u.upcoming.map((a, i) => (
                        <span key={i} className="mr-2 inline-block">{KIND_LABEL[a.kind] === "Vacaciones" ? "🌴" : "📋"} {rangeEs(a.start, a.end)}{a.status === "pendiente" ? " (pend.)" : ""}</span>
                      ))}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent></Card>
        )}
      </section>

      {decision && <VacDecisionDialog decision={decision} onClose={() => setDecision(null)} onConfirm={confirmDecision} saving={saving} />}
    </>
  );
}

function VacDecisionDialog({ decision, onClose, onConfirm, saving }) {
  const isApprove = decision.mode === "approve";
  const [message, setMessage] = useState("");
  const [justificanteRequired, setJustificanteRequired] = useState(false);
  const it = decision.item;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" data-testid="vac-decision-dialog">
        <DialogHeader>
          <DialogTitle>{isApprove ? "Aprobar solicitud" : "Rechazar solicitud"}</DialogTitle>
          <DialogDescription>{isApprove ? "Puedes añadir un mensaje. La persona recibirá un email con tu decisión." : "Indica el motivo. La persona recibirá un email con tu decisión y el motivo."}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {it?.user?.name} · {KIND_LABEL[it?.kind]}{it?.is_partial ? " (por horas)" : ""} · {rangeEs(it?.start, it?.end)}
          {it?.is_partial && it?.time_start ? ` · ${it.time_start}–${it.time_end}` : ""}
        </p>
        <div className="space-y-1.5">
          <Label>{isApprove ? "Mensaje / aclaración (opcional)" : "Motivo (opcional)"}</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder={isApprove ? "Ej.: aprobado, disfruta." : "Motivo del rechazo"} data-testid="vac-decision-message" />
        </div>
        {isApprove && (
          <label className="flex items-start gap-2 rounded-md border p-2 text-xs cursor-pointer hover:bg-muted/40" data-testid="vac-decision-justif-toggle">
            <input type="checkbox" checked={justificanteRequired} onChange={(e) => setJustificanteRequired(e.target.checked)} className="mt-0.5" />
            <div>
              <p className="font-medium">Requerir justificante</p>
              <p className="text-[11px] text-muted-foreground">Al aprobar, la persona deberá subir un PDF/imagen/Word como justificante (máx 10 MB). Verás pendientes y subidos en el panel de justificantes.</p>
            </div>
          </label>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button className={isApprove ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}
                  onClick={() => onConfirm(message, isApprove ? justificanteRequired : undefined)}
                  disabled={saving} data-testid="vac-decision-confirm">{saving ? "Enviando…" : (isApprove ? "Aprobar y enviar" : "Rechazar y enviar")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
